import { KeyboardKeyEnum } from '@/React/Shared/Type/KeyboardKeyEnum'
import { useEffect, useId, useRef, useState } from 'react'
import { Button } from '@/React/Shared/Component/Button'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'
import { TextInput } from '@/React/Shared/Component/Forms/TextInput'

export interface SearcheableEntity {
    id: string
    value: string
    displayText: string
}

export interface SelectSearchInputProps {
    list: SearcheableEntity[]
    name?: string | null
    id?: string
    defaultValue?: string | null
    useBookingFormThemeCssVariables?: boolean
    disabled?: boolean
    placeholder?: string
    className?: string
    isOpen?: boolean
    'aria-label'?: string
    'aria-labelledby'?: string
    onChange?: (value: string) => void
}

export function handleSelectSearchTriggerKeyDown({
    event,
    disabled,
    onOpen,
}: {
    event: React.KeyboardEvent<HTMLElement>
    disabled: boolean
    onOpen: () => void
}): boolean {
    if (disabled) {
        return false
    }

    if (
        event.key === KeyboardKeyEnum.Enter ||
        event.key === KeyboardKeyEnum.Space ||
        event.key === KeyboardKeyEnum.ArrowDown ||
        event.key === KeyboardKeyEnum.ArrowUp
    ) {
        event.preventDefault()
        onOpen()

        return true
    }

    return false
}

export function handleSelectSearchInputKeyDown({
    event,
    filteredList,
    highlightedIndex,
    setHighlightedIndex,
    onSelect,
    onClose,
}: {
    event: React.KeyboardEvent<HTMLInputElement>
    filteredList: SearcheableEntity[]
    highlightedIndex: number
    setHighlightedIndex: (updater: number | ((prev: number) => number)) => void
    onSelect: (value: string) => void
    onClose: () => void
}): boolean {
    if (event.key === KeyboardKeyEnum.ArrowDown) {
        event.preventDefault()

        if (filteredList.length > 0) {
            setHighlightedIndex((prev) => (prev + 1) % filteredList.length)
        }

        return true
    }

    if (event.key === KeyboardKeyEnum.ArrowUp) {
        event.preventDefault()

        if (filteredList.length > 0) {
            setHighlightedIndex((prev) =>
                prev <= 0 ? filteredList.length - 1 : prev - 1,
            )
        }

        return true
    }

    if (event.key === KeyboardKeyEnum.Home) {
        event.preventDefault()

        if (filteredList.length > 0) {
            setHighlightedIndex(0)
        }

        return true
    }

    if (event.key === KeyboardKeyEnum.End) {
        event.preventDefault()

        if (filteredList.length > 0) {
            setHighlightedIndex(filteredList.length - 1)
        }

        return true
    }

    if (event.key === KeyboardKeyEnum.Enter) {
        event.preventDefault()

        if (highlightedIndex >= 0 && filteredList[highlightedIndex]) {
            onSelect(filteredList[highlightedIndex].value)
            return true
        }

        if (filteredList.length === 1) {
            onSelect(filteredList[0].value)
            return true
        }

        return false
    }

    if (event.key === KeyboardKeyEnum.Escape) {
        event.preventDefault()
        onClose()

        return true
    }

    if (event.key === KeyboardKeyEnum.Tab) {
        onClose()

        return true
    }

    return false
}

export function SelectSearchInput({
    list,
    name,
    id,
    defaultValue,
    useBookingFormThemeCssVariables = false,
    disabled = false,
    placeholder = 'Select an option...',
    className = '',
    isOpen = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    onChange,
}: SelectSearchInputProps) {
    const generatedId = useId()
    const inputId = id ?? (name ? name : generatedId)
    const listboxId = `${inputId}-listbox`
    const searchInputId = `${inputId}-search`

    const [searchTerm, setSearchTerm] = useState<string>('')
    const [isExpanded, setIsExpanded] = useState<boolean>(isOpen)

    useEffect(() => {
        setIsExpanded(isOpen)
    }, [isOpen])

    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const optionRefs = useRef<(HTMLDivElement | null)[]>([])

    const [internalValue, setInternalValue] = useState<string>(
        defaultValue ?? '',
    )

    const selectedEntity = list.find((item) => item.value === internalValue)

    const filteredList = searchTerm
        ? list.filter((item) =>
              item.displayText.toLowerCase().includes(searchTerm.toLowerCase()),
          )
        : list

    const initialMatchIndex = isOpen
        ? filteredList.findIndex((item) => item.value === internalValue)
        : -1
    const [highlightedIndex, setHighlightedIndex] = useState<number>(
        initialMatchIndex >= 0 ? initialMatchIndex : isOpen ? 0 : -1,
    )

    useEffect(() => {
        if (!isExpanded) {
            return
        }

        const handleDocumentClick = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setIsExpanded(false)
                setHighlightedIndex(-1)
            }
        }

        document.addEventListener('click', handleDocumentClick)

        return () => {
            document.removeEventListener('click', handleDocumentClick)
        }
    }, [isExpanded])

    useEffect(() => {
        if (isExpanded) {
            searchInputRef.current?.focus()

            const matchIndex = filteredList.findIndex(
                (item) => item.value === internalValue,
            )

            setHighlightedIndex(matchIndex >= 0 ? matchIndex : 0)
        } else {
            setHighlightedIndex(-1)
            setSearchTerm('')
        }
    }, [isExpanded])

    useEffect(() => {
        if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
            optionRefs.current[highlightedIndex]?.scrollIntoView({
                block: 'nearest',
            })
        }
    }, [highlightedIndex])

    const handleToggle = () => {
        if (disabled) {
            return
        }

        setIsExpanded((prev) => !prev)
    }

    const handleItemSelectionChange = (itemValue: string) => {
        setInternalValue(itemValue)
        onChange?.(itemValue)
        setIsExpanded(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
        triggerRef.current?.focus()
    }

    const resetResults = () => {
        setSearchTerm('')
        setHighlightedIndex(0)

        if (searchInputRef.current) {
            searchInputRef.current.value = ''
            searchInputRef.current.focus()
        }
    }

    const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        handleSelectSearchTriggerKeyDown({
            event: e,
            disabled,
            onOpen: () => setIsExpanded(true),
        })
    }

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        handleSelectSearchInputKeyDown({
            event: e,
            filteredList,
            highlightedIndex,
            setHighlightedIndex,
            onSelect: (itemValue) => handleItemSelectionChange(itemValue),
            onClose: () => {
                setIsExpanded(false)
                setSearchTerm('')
                setHighlightedIndex(-1)
                triggerRef.current?.focus()
            },
        })
    }

    const stateClasses = disabled
        ? 'bg-stone-100 text-gray-500 select-none cursor-not-allowed'
        : 'bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary'

    const activeDescendantId =
        isExpanded && highlightedIndex >= 0 && filteredList[highlightedIndex]
            ? `${listboxId}-option-${filteredList[highlightedIndex].id}`
            : undefined

    return (
        <div
            ref={containerRef}
            className={className}
        >
            <div
                ref={triggerRef}
                id={inputId}
                role={isExpanded ? undefined : 'combobox'}
                tabIndex={isExpanded ? -1 : disabled ? -1 : 0}
                aria-expanded={isExpanded ? undefined : false}
                aria-haspopup={isExpanded ? undefined : 'listbox'}
                aria-controls={isExpanded ? undefined : listboxId}
                aria-label={isExpanded ? undefined : ariaLabel}
                aria-labelledby={isExpanded ? undefined : ariaLabelledBy}
                aria-disabled={
                    isExpanded ? undefined : disabled ? true : undefined
                }
                onClick={handleToggle}
                onKeyDown={handleTriggerKeyDown}
                className={`border border-stone-200 py-2 px-3 rounded-lg2 ${stateClasses}`.trim()}
            >
                <div className="flex items-center justify-between">
                    <div>
                        {selectedEntity
                            ? selectedEntity.displayText
                            : placeholder}
                    </div>
                    {!disabled && (
                        <div>
                            <Icon
                                name={
                                    isExpanded
                                        ? IconNameEnum.AngleUp
                                        : IconNameEnum.AngleDown
                                }
                                aria-hidden="true"
                                className="w-4 h-4 text-gray-500"
                            />
                        </div>
                    )}
                </div>

                {isExpanded && (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="mt-2 space-y-2"
                    >
                        <div className="flex items-center gap-1">
                            <TextInput
                                ref={searchInputRef}
                                id={searchInputId}
                                role="combobox"
                                aria-expanded={true}
                                aria-haspopup="listbox"
                                aria-controls={listboxId}
                                aria-autocomplete="list"
                                aria-activedescendant={activeDescendantId}
                                aria-label={ariaLabel}
                                aria-labelledby={ariaLabelledBy}
                                defaultValue={searchTerm}
                                placeholder="Search"
                                onChange={(e) => {
                                    setSearchTerm(e.target.value)
                                    setHighlightedIndex(0)
                                }}
                                onKeyDown={handleSearchKeyDown}
                                useBookingFormThemeCssVariables={
                                    useBookingFormThemeCssVariables
                                }
                            />
                            <Button
                                type="button"
                                color="light"
                                aria-label="Clear search"
                                title="Clear search"
                                iconName={IconNameEnum.ArrowRotate}
                                iconClass="w-4 h-4 text-gray-500"
                                onClick={resetResults}
                            />
                        </div>
                        <div
                            id={listboxId}
                            role="listbox"
                            aria-label={ariaLabel}
                            aria-labelledby={ariaLabelledBy}
                            tabIndex={-1}
                            className="max-h-48 min-h-48 overflow-y-auto"
                        >
                            {filteredList.length > 0 ? (
                                filteredList.map((item, index) => {
                                    const isSelected =
                                        item.value === internalValue
                                    const isHighlighted =
                                        index === highlightedIndex
                                    const optionId = `${listboxId}-option-${item.id}`

                                    return (
                                        <div
                                            key={item.id}
                                            id={optionId}
                                            ref={(el) => {
                                                optionRefs.current[index] = el
                                            }}
                                            role="option"
                                            aria-selected={isSelected}
                                            onClick={() =>
                                                handleItemSelectionChange(
                                                    item.value,
                                                )
                                            }
                                            onMouseEnter={() =>
                                                setHighlightedIndex(index)
                                            }
                                            className={`p-2 rounded-lg2 text-sm cursor-pointer transition-colors ${
                                                isHighlighted
                                                    ? 'bg-primary-shade-ligth text-primary font-medium'
                                                    : 'hover:bg-gray-100 text-gray-800'
                                            } ${isSelected ? 'font-semibold' : ''}`.trim()}
                                        >
                                            {item.displayText}
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="text-center italic text-gray-500 text-sm py-4">
                                    No results...
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {name && (
                    <input
                        type="hidden"
                        name={name}
                        value={internalValue}
                    />
                )}
            </div>
        </div>
    )
}
